import {
  ApiRef,
  ConfigApi,
  createApiRef,
  DiscoveryApi,
} from '@backstage/core-plugin-api';
import { AxiosInstance } from 'axios';
import { AxiosInstanceProviderApi } from './axiosInstanceApi';
import { getDynamicCode } from '../components/Codegen/constants';

const DEFAULT_PROXY_PATH_BASE = '';

type Options = {
  discoveryApi: DiscoveryApi;
  /**
   * Path to use for requests via the proxy, defaults to ''
   */
  proxyPathBase?: string;
  configApi: ConfigApi;
  axiosInstanceProviderApi: AxiosInstanceProviderApi;
};
export interface DesignToCodeApi {
  fetchDocuments: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    docId?: string,
  ) => Promise<any>;
  updateDocument: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    docId?: string,
    data?: any,
  ) => Promise<any>;
  deleteDocument: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    docId: string,
  ) => Promise<any>;
  fetchDocumentsAdminMode: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
  ) => Promise<any>;
  uploadDocument: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    data: any,
  ) => Promise<any>;
  deleteDocumentAdminMode: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    docId: string,
  ) => Promise<any>;
  fetchAllUsersInfo(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
  ): Promise<any>;
  fetchUserIfoById: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    id: string,
  ) => Promise<any>;
  updateUserIfoById: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    id: string,
  ) => Promise<any>;
  deleteUserIfoById: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    id: string,
  ) => Promise<any>;
  fetchUserIfoByEmail: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
  ) => Promise<any>;
  insertUserIfo: (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    id: string,
  ) => Promise<any>;
  importFigmaDesign: (fileKey: string, nodeId: string) => Promise<any>;
  fetchImageFromFigmaDesign: (fileKey: string, nodeId: string) => Promise<any>;
  setAuthorizationKey(authorizationKey: string): void;
  isAuthorizationKeySet(): boolean;
  updateDesignMetrics: (data: any) => Promise<any>;
  getDefaultMetricsPayload(): any;
  setFigmaApiTokenAndURL: (apiToken: string, apiBaseUrl: string) => void;
}

export const designToCodeRef = createApiRef<DesignToCodeApi>({
  id: 'cbre.devx.api.designtocode',
});

export class DesignToCodeApiImpl implements DesignToCodeApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly proxyPathBase: string;
  private readonly axiosInstancePromise: Promise<AxiosInstance>;
  private coreURL: string = 'service/secure/v1/design-to-code';
  private authorizationKey = '';
  apiToken: string = '';
  apiBaseUrl: string = '';

  constructor(options: Options) {
    this.discoveryApi = options.discoveryApi;
    this.proxyPathBase = options.proxyPathBase || DEFAULT_PROXY_PATH_BASE;
    this.axiosInstancePromise = options.axiosInstanceProviderApi.getInstance();
  }

  private async getApiUrl() {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    return `${baseUrl}${this.proxyPathBase}/figma`;
  }

  public isAuthorizationKeySet(): boolean {
    return this.authorizationKey !== '';
  }

  public setAuthorizationKey(authorizationKey: string) {
    this.authorizationKey = `Basic ${authorizationKey}`;
  }

  getHeaders = (
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
  ) => {
    return {
      'Content-Type': 'application/json',
      Authorization: authorizationKey || this.authorizationKey,
      'X-User-Email': profEmail,
      'X-User-Name': profileName,
    };
  };

  async updateDocument(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    docId?: string,
    data?: any,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .put(`${this.coreURL}/documents/${docId}`, data, {
        headers: this.getHeaders(profEmail, profileName, authorizationKey),
      })
      .then(res => res.data);
  }

  async fetchDocumentsAdminMode(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`${this.coreURL}/adm/documents`, {
        headers: this.getHeaders(profEmail, profileName, authorizationKey),
      })
      .then(res => res.data);
  }

  async fetchDocuments(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    docId?: string,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(
        docId
          ? `${this.coreURL}/documents/${docId}`
          : `${this.coreURL}/documents?email=${profEmail}`,
        {
          headers: this.getHeaders(profEmail, profileName, authorizationKey),
        },
      )
      .then(res => res.data);
  }

  async uploadDocument(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    data: any,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .post(`${this.coreURL}/documents`, data, {
        headers: this.getHeaders(profEmail, profileName, authorizationKey),
      })
      .then(res => res.data);
  }

  async deleteDocument(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    docId: string,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .delete(`${this.coreURL}/documents/${docId}`, {
        headers: this.getHeaders(profEmail, profileName, authorizationKey),
      })
      .then(res => res.data);
  }

  async deleteDocumentAdminMode(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    docId: string,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .delete(`${this.coreURL}/adm/documents/${docId}`, {
        headers: this.getHeaders(profEmail, profileName, authorizationKey),
      })
      .then(res => res.data);
  }

  async fetchAllUsersInfo(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`${this.coreURL}/adm/users/all`, {
        headers: this.getHeaders(profEmail, profileName, authorizationKey),
      })
      .then(res => res.data);
  }

  async fetchUserIfoById(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    id: string,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`${this.coreURL}/users/${id}`, {
        headers: this.getHeaders(profEmail, profileName, authorizationKey),
      })
      .then(res => res.data);
  }

  async updateUserIfoById(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    id: string,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .put(
        `${this.coreURL}/users/${id}`,
        {
          email: profEmail,
          fullName: profileName,
        },
        {
          headers: this.getHeaders(profEmail, profileName, authorizationKey),
        },
      )
      .then(res => res.data);
  }

  async deleteUserIfoById(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    id: string,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .delete(`${this.coreURL}/users/${id}`, {
        headers: this.getHeaders(profEmail, profileName, authorizationKey),
      })
      .then(res => res.data);
  }

  async fetchUserIfoByEmail(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`${this.coreURL}/users?email=${profEmail}`, {
        headers: this.getHeaders(profEmail, profileName, authorizationKey),
      })
      .then(res => res.data);
  }

  async insertUserIfo(
    profEmail: string,
    profileName: string,
    authorizationKey: string | null,
    id: string,
  ) {
    const instance = await this.axiosInstancePromise;
    return instance
      .post(
        `${this.coreURL}/users`,
        {
          id,
          userDetails: {
            email: profEmail,
            fullName: profileName,
          },
        },
        {
          headers: this.getHeaders(profEmail, profileName, authorizationKey),
        },
      )
      .then(res => res.data);
  }

  setFigmaApiTokenAndURL(apiToken: string, apiBaseUrl: string) {
    this.apiToken = apiToken;
    this.apiBaseUrl = apiBaseUrl;
  }
  async importFigmaDesign(fileKey: string, nodeId: string): Promise<any> {
    return fetch(`${this.apiBaseUrl}/files/${fileKey}/nodes?ids=${nodeId}`, {
      headers: {
        'X-Figma-Token': this.apiToken,
      },
    }).then(response => response.json());
  }

  async fetchImageFromFigmaDesign(
    fileKey: string,
    nodeId: string,
  ): Promise<any> {
    const url = await this.getApiUrl();
    return fetch(`${url}/images/${fileKey}?ids=${nodeId}&format=svg`).then(
      response => response.json(),
    );
  }
  getDefaultMetricsPayload() {
    return {
      applicationname: 'design_to_code',
      extid: getDynamicCode(true),
      action: '',
      emailid: '',
      additionaldata: {
        content: {
          description: '',
          title: '',
        },
      },
      clientadditionaldata: {
        apmNumber: 'APM0001750',
        language: 'en',
        projectId: '11666',
        projectName: 'DEVX',
        // savedItemCount: 1,
        // savedItemTokenCount: 306,
        toolName: 'DesignToCode',
        workItemType: '',
        workitemId: '',
      },
      codeGenerationMetrics: {
        figma_import_id: 0,
        session_id: '',
        framework: '',
        language: '',
        style: '',
        version: '',
        //unitTests: '0',
        code_gen_id: '',
        totalLinesOfCode: 0,
        totalFiles: 0,
        totalComponents: 0,
        user_prompt: '',
      },
      codeAcceptanceMetrics: {
        session_id: '',
        code_gen_id: '',
        accepted_id: '',
        acceptance_type: '',
        lines_accepted: 0,
        files_accepted: 0,
      },
      dashboard: {
        figma_import_id: 0,
        session_id: '',
        framework: '',
        language: '',
        style: '',
        unitTests: 'jest',
        code_gen_id: '',
        totalLinesOfCode: 0,
        totalFiles: 0,
        totalComponents: 0,
        user_name: '',
        user_email: '',
        user_role: '',
        //user_id: '',
      },
    };
  }
  async updateDesignMetrics(data: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .post(`/service/discovery/secure/metrics/logusage`, data, {
        headers: {
          'api-version': 1.0,
          clientid: '13655a6f-8351-48e0-b7d7-4cb695f284ab',
          'Content-Type': 'application/json',
          Authorization: this.authorizationKey,
        },
      })
      .then(res => res.data);
  }
}
